import { t } from '@/lib/i18n';
import { Database, HardDrive, Image as ImageIcon, Layers, Settings as SettingsIcon, Sparkles } from 'lucide-react';
import { CenteredSpinner } from '@/components/ui';
import { formatFileSize } from '@/lib/format';
import { useLibraryStats } from '@/lib/queries';

/** Admin overview: library size, storage, type breakdown, tagging backlog. */
export function SettingsPage() {
  const { data: stats, isPending } = useLibraryStats();
  if (isPending || !stats) return <CenteredSpinner label={t('settingsPage.loading')} />;

  const kinds = Object.entries(stats.byKind).sort((a, b) => b[1] - a[1]);

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-6">
        <div className="mb-5 flex items-center gap-2">
          <SettingsIcon className="size-5 text-brand-500" />
          <h1 className="text-lg font-semibold text-strong">{t('settingsPage.title')}</h1>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat icon={<Layers className="size-4" />} label={t('settingsPage.totalAssets')} value={stats.total.toLocaleString()} />
          <Stat icon={<HardDrive className="size-4" />} label={t('settingsPage.storageUsed')} value={formatFileSize(stats.storageBytes)} />
          <Stat icon={<Sparkles className="size-4" />} label={t('settingsPage.untagged')} value={stats.untagged.toLocaleString()} />
          <Stat icon={<ImageIcon className="size-4" />} label={t('settingsPage.images')} value={(stats.byKind.image ?? 0).toLocaleString()} />
        </div>

        <section className="mt-6 rounded-xl border line surface-card p-5">
          <div className="mb-3 flex items-center gap-2">
            <Database className="size-4 text-brand-500" />
            <h2 className="text-sm font-semibold text-strong">{t('settingsPage.assetsByType')}</h2>
          </div>
          {kinds.length === 0 ? (
            <p className="text-sm text-muted">{t('settingsPage.noAssets')}</p>
          ) : (
            <div className="space-y-2">
              {kinds.map(([kind, n]) => {
                const pct = stats.total ? Math.round((n / stats.total) * 100) : 0;
                return (
                  <div key={kind} className="flex items-center gap-3 text-sm">
                    <span className="w-24 shrink-0 capitalize text-body">{kind}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full surface-sunken">
                      <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-16 shrink-0 text-right tabular-nums text-muted">{n} · {pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-4 rounded-xl border line surface-card p-5 text-sm text-muted">
          <h2 className="mb-1 text-sm font-semibold text-strong">{t('settingsPage.deployment')}</h2>
          <p>{t('settingsPage.deploymentIntro')} <code className="text-faint">.env</code>{t('settingsPage.deploymentOutro')}</p>
        </section>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl border line surface-card p-4">
      <div className="mb-1 flex items-center gap-1.5 text-muted">{icon}<span className="text-xs">{label}</span></div>
      <div className="text-xl font-semibold text-strong tabular-nums">{value}</div>
    </div>
  );
}
